import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import noImage from "../../../assets/images/common/importerr-no-image-logo.png";
import { TbZoomScan } from "react-icons/tb";
import { FaSpinner, FaExternalLinkAlt, FaStar, FaStore, FaBox, FaRuler, FaWeight, FaSync } from "react-icons/fa";
import { fetchToken } from "../../../lib/globalApiClient";
import "./amazonProduct.css";

const BACKEND_API = process.env.REACT_APP_APIUrl || 'https://api.importerr.com/api/';
const DEFAULT_PRICING_DOMAIN = 'importerr.com_common';

// Use onePiecePrice (one-piece) as primary RMB source for all UI pricing.
const getOnePieceRmbPrice = (item, fallback = 0) => {
  const value = parseFloat(
    item?.fenxiaoPriceInfo?.onePiecePrice ??
    item?.onePiecePrice ??
    item?.priceInfo?.onePiecePrice ??
    item?.priceInfo?.price ??
    item?.price ??
    item?.salePrice ??
    item?.retailPrice ??
    item?.ap ??
    0
  );

  if (Number.isFinite(value) && value > 0) return value;

  const fallbackValue = parseFloat(fallback || 0);
  return Number.isFinite(fallbackValue) && fallbackValue > 0 ? fallbackValue : 0;
};

export default function SellerAmazonProductDetail({ asin: asinProp, isEmbedded = false }) {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const asinFromQuery = asinProp || searchParams.get("asin") || "";

  // Check if opened from Product Research (hide ASIN Map button)
  const isFromResearch = isEmbedded || searchParams.get("from") === "research";

  // Check if a product was selected from similar products
  const isSelectedProductMode = searchParams.get("selectedProduct") === "true";
  const selectedOfferId = searchParams.get("offerId") || "";
  const selectedTitle = decodeURIComponent(searchParams.get("title") || "");
  const selectedImage = decodeURIComponent(searchParams.get("image") || "");
  const selectedRmb = parseFloat(searchParams.get("rmb") || 0);
  const selectedInr = parseFloat(searchParams.get("inr") || 0);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [activeImageIdx, setActiveImageIdx] = useState(0);
  const [activeTab, setActiveTab] = useState("description");

  // Similar products state
  const [similarProducts, setSimilarProducts] = useState([]);
  const [loadingSimilar, setLoadingSimilar] = useState(false);
  const [showSimilar, setShowSimilar] = useState(!isSelectedProductMode); // Hide sidebar if product selected
  const [pricingCache, setPricingCache] = useState({});
  const pricingCacheRef = useRef({}); // Ref to avoid re-renders when checking cache
  const [failedOrderAmount, setFailedOrderAmount] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [sortOption, setSortOption] = useState('default'); // Sorting: default, price-low, price-high, margin-low, margin-high
  const [findSimilarProgress, setFindSimilarProgress] = useState(0);
  const [findSimilarStatus, setFindSimilarStatus] = useState('');

  // Selected product details state
  const [selectedProductDetails, setSelectedProductDetails] = useState(null);
  const [selectedProductVariants, setSelectedProductVariants] = useState([]);
  const [loadingSelectedProduct, setLoadingSelectedProduct] = useState(false);
  const [selectedVariantImageIdx, setSelectedVariantImageIdx] = useState(0);
  const hasFetchedSelectedProduct = useRef(false); // Prevent duplicate fetches

  // Confirmation modal state (before mapping)
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmVariant, setConfirmVariant] = useState(null);
  const [mappingRemark, setMappingRemark] = useState("");
  const [isMapping, setIsMapping] = useState(false);

  // HSN mapping state
  const [hsnCategories, setHsnCategories] = useState([]);
  const [selectedHsnCategory, setSelectedHsnCategory] = useState('');
  const [hsnMappings, setHsnMappings] = useState([]);
  const [selectedHsnMapping, setSelectedHsnMapping] = useState(null);
  const [loadingHsn, setLoadingHsn] = useState(false);

  // Sort similar products based on selected option
  const sortedSimilarProducts = useMemo(() => {
    if (!similarProducts || similarProducts.length === 0) return [];

    const avgOrderValue = failedOrderAmount ? failedOrderAmount.totalAmount / failedOrderAmount.orderCount : 0;

    const productsWithMargin = similarProducts.map(product => {
      const marginPerOrder = avgOrderValue - (product.price?.inr || 0);
      return { ...product, marginPerOrder };
    });

    switch (sortOption) {
      case 'price-low':
        return [...productsWithMargin].sort((a, b) => (a.price?.inr || 0) - (b.price?.inr || 0));
      case 'price-high':
        return [...productsWithMargin].sort((a, b) => (b.price?.inr || 0) - (a.price?.inr || 0));
      case 'margin-low':
        return [...productsWithMargin].sort((a, b) => a.marginPerOrder - b.marginPerOrder);
      case 'margin-high':
        return [...productsWithMargin].sort((a, b) => b.marginPerOrder - a.marginPerOrder);
      default:
        return productsWithMargin;
    }
  }, [similarProducts, sortOption, failedOrderAmount]);

  // Fetch HSN categories on mount (with fallback to fetch all mappings)
  useEffect(() => {
    const fetchHsnData = async () => {
      try {
        const token = localStorage.getItem('LOGIN_HEADER');

        // First try to get categories
        let categoriesResponse = await fetch(`${BACKEND_API}order/hsn-mappings/categories`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (categoriesResponse.ok) {
          const result = await categoriesResponse.json();
          console.log('[HSN] Categories fetched:', result);
          if (result.data && result.data.length > 0) {
            setHsnCategories(result.data);
            return;
          }
        }

        // Fallback: fetch all HSN mappings and extract unique categories
        console.log('[HSN] Trying fallback - fetching all mappings');
        const allMappingsResponse = await fetch(`${BACKEND_API}order/hsn-mappings`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (allMappingsResponse.ok) {
          const result = await allMappingsResponse.json();
          console.log('[HSN] All mappings fetched:', result);
          const mappings = result.data || [];
          // Extract unique categories
          const uniqueCategories = [...new Set(mappings.map(m => m.category))].filter(Boolean).sort();
          console.log('[HSN] Extracted categories:', uniqueCategories);
          setHsnCategories(uniqueCategories);
        } else {
          console.error('[HSN] Failed to fetch all mappings:', allMappingsResponse.status);
        }
      } catch (err) {
        console.error('[HSN] Error fetching HSN data:', err);
      }
    };
    fetchHsnData();
  }, []);

  // Fetch HSN mappings when category changes
  useEffect(() => {
    const fetchHsnMappings = async () => {
      if (!selectedHsnCategory) {
        setHsnMappings([]);
        setSelectedHsnMapping(null);
        return;
      }
      setLoadingHsn(true);
      try {
        const token = localStorage.getItem('LOGIN_HEADER');
        const response = await fetch(`${BACKEND_API}order/hsn-mappings/category/${encodeURIComponent(selectedHsnCategory)}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        if (response.ok) {
          const result = await response.json();
          console.log('[HSN] Mappings for category:', selectedHsnCategory, result);
          const mappings = result.data || [];
          setHsnMappings(mappings);
          // Auto-select first mapping when category is selected
          if (mappings.length > 0) {
            setSelectedHsnMapping(mappings[0]);
          } else {
            setSelectedHsnMapping(null);
          }
        } else {
          console.error('[HSN] Failed to fetch mappings:', response.status);
          // Fallback: filter from all mappings
          const allResponse = await fetch(`${BACKEND_API}order/hsn-mappings?category=${encodeURIComponent(selectedHsnCategory)}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          if (allResponse.ok) {
            const result = await allResponse.json();
            const mappings = result.data || [];
            setHsnMappings(mappings);
            // Auto-select first mapping
            if (mappings.length > 0) {
              setSelectedHsnMapping(mappings[0]);
            }
          }
        }
      } catch (err) {
        console.error('[HSN] Error fetching mappings:', err);
      } finally {
        setLoadingHsn(false);
      }
    };
    fetchHsnMappings();
  }, [selectedHsnCategory]);

  // Calculate INR price using a fixed default pricing domain.
  const calculateINRPrice = useCallback(async (rmbPrice, offerId = '', skuId = '', weight = 0, dimensions = null) => {
    if (!rmbPrice || rmbPrice <= 0) return { inr: 0, breakdown: null };

    const useDomain = DEFAULT_PRICING_DOMAIN;

    // Calculate volumetric weight if dimensions provided
    let volumetricWeight = 0;
    if (dimensions) {
      const { length = 0, width = 0, height = 0 } = dimensions;
      if (length > 0 && width > 0 && height > 0) {
        volumetricWeight = (length * width * height) / 5000;
      }
    }

    const deadWeight = weight || 0.5; // Default weight if not provided
    const cacheKey = `${rmbPrice.toFixed(2)}_${offerId}_${skuId}_${useDomain}`;

    // Check cache using ref (doesn't cause re-renders)
    if (pricingCacheRef.current[cacheKey]) {
      return pricingCacheRef.current[cacheKey];
    }

    try {
      // Use simple calculate endpoint with selected domain (display only)
      const response = await fetch(`${BACKEND_API}product/pricing-algorithms/calculate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ap: parseFloat(rmbPrice),
          ls: 0,
          hsn: false,
          hsnTaxPercent: 18,
          dw: deadWeight,
          vw: volumetricWeight,
          sku: String(offerId || ''),
          testDomain: useDomain
        })
      });

      if (!response.ok) return { inr: 0, breakdown: null };

      const result = await response.json();
      const inrPrice = parseFloat(result?.data?.breakdown?.FBP || result?.breakdown?.FBP || 0);
      const breakdown = result?.data?.breakdown || result?.breakdown || null;

      const priceData = {
        inr: inrPrice,
        breakdown,
        deadWeight,
        volumetricWeight,
        dimensions
      };

      // Update both ref and state
      pricingCacheRef.current[cacheKey] = priceData;
      setPricingCache(prev => ({ ...prev, [cacheKey]: priceData }));
      return priceData;
    } catch { return { inr: 0, breakdown: null }; }
  }, []);

  // Helper: Fetch full product details and extract minimum variant price
  const fetchProductDetailsWithVariants = useCallback(async (searchResultItem) => {
    try {
      const baseUrl = process.env.REACT_APP_APIUrl1688;
      const token = await fetchToken();
      const offerId = searchResultItem.offerId || searchResultItem.id || '';

      if (!offerId) {
        // If no offerId, return basic product with original price
        return {
          id: `p-${Math.random()}`,
          offerId: offerId,
          title: searchResultItem.subjectTrans || searchResultItem.subject || searchResultItem.title || 'Product',
          image: searchResultItem.imageUrl || searchResultItem.image || '',
          price: {
            rmb: getOnePieceRmbPrice(searchResultItem, 0),
            inr: 0
          },
          variantCount: 1,
          supplier: searchResultItem.companyName || searchResultItem.supplier || 'Supplier',
          monthSold: searchResultItem.monthSold || 0,
          moq: searchResultItem.moq || searchResultItem.minOrderQuantity || 1,
          rating: searchResultItem.sellerRating || searchResultItem.rating || null,
        };
      }

      const response = await fetch(baseUrl + '/product-detail-by-offerid', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Token': token
        },
        body: JSON.stringify({ offerId: offerId.toString(), country: 'en' })
      });

      if (!response.ok) {
        // Fallback to basic product if detail fetch fails
        console.warn('Failed to fetch product details for', offerId);
        return {
          id: offerId || `p-${Math.random()}`,
          offerId,
          title: searchResultItem.subjectTrans || searchResultItem.subject || searchResultItem.title || 'Product',
          image: searchResultItem.imageUrl || searchResultItem.image || '',
          price: {
            rmb: getOnePieceRmbPrice(searchResultItem, 0),
            inr: 0
          },
          variantCount: 1,
          supplier: searchResultItem.companyName || searchResultItem.supplier || 'Supplier',
          monthSold: searchResultItem.monthSold || 0,
          moq: searchResultItem.moq || searchResultItem.minOrderQuantity || 1,
          rating: searchResultItem.sellerRating || searchResultItem.rating || null,
        };
      }

      const data = await response.json();
      const productData = data.result?.result || data.product || data.data || data;

      // Extract all variants from possible sources
      let skuInfos = [];
      if (Array.isArray(productData.productSkuInfos) && productData.productSkuInfos.length > 0) {
        skuInfos = productData.productSkuInfos;
      } else if (Array.isArray(productData.skuInfos) && productData.skuInfos.length > 0) {
        skuInfos = productData.skuInfos;
      } else if (Array.isArray(productData.skuList) && productData.skuList.length > 0) {
        skuInfos = productData.skuList;
      } else if (Array.isArray(productData.skus) && productData.skus.length > 0) {
        skuInfos = productData.skus;
      } else if (Array.isArray(productData.productInfo?.skuInfos) && productData.productInfo.skuInfos.length > 0) {
        skuInfos = productData.productInfo.skuInfos;
      } else if (Array.isArray(productData.data?.productSkuInfos) && productData.data.productSkuInfos.length > 0) {
        skuInfos = productData.data.productSkuInfos;
      } else if (Array.isArray(productData.variants) && productData.variants.length > 0) {
        skuInfos = productData.variants;
      }

      // Find minimum RMB price among all variants
      let minRmbPrice = getOnePieceRmbPrice(searchResultItem, 0);
      if (skuInfos.length > 0) {
        const prices = skuInfos.map(sku =>
          getOnePieceRmbPrice(sku, 0)
        ).filter(p => p > 0);

        if (prices.length > 0) {
          minRmbPrice = Math.min(...prices);
        }
      }

      // Calculate INR price for minimum RMB price
      const minWeight = parseFloat(searchResultItem.weight || searchResultItem.productWeight || 0.5);
      const minDimensions = searchResultItem.dimensions || searchResultItem.productDimensions || null;
      const priceData = await calculateINRPrice(minRmbPrice, offerId, '', minWeight, minDimensions);

      return {
        id: offerId || `p-${Math.random()}`,
        offerId,
        title: searchResultItem.subjectTrans || searchResultItem.subject || searchResultItem.title || 'Product',
        image: searchResultItem.imageUrl || searchResultItem.image || '',
        price: {
          rmb: minRmbPrice,
          inr: priceData.inr,
          breakdown: priceData.breakdown
        },
        variantCount: skuInfos.length || 1,
        supplier: searchResultItem.companyName || searchResultItem.supplier || 'Supplier',
        monthSold: searchResultItem.monthSold || 0,
        moq: searchResultItem.moq || searchResultItem.minOrderQuantity || 1,
        rating: searchResultItem.sellerRating || searchResultItem.rating || null,
      };
    } catch (err) {
      console.error('Error fetching product details:', err);
      // Fallback to basic product
      return {
        id: searchResultItem.offerId || searchResultItem.id || `p-${Math.random()}`,
        offerId: searchResultItem.offerId || searchResultItem.id || '',
        title: searchResultItem.subjectTrans || searchResultItem.subject || searchResultItem.title || 'Product',
        image: searchResultItem.imageUrl || searchResultItem.image || '',
        price: {
          rmb: getOnePieceRmbPrice(searchResultItem, 0),
          inr: 0
        },
        variantCount: 1,
        supplier: searchResultItem.companyName || searchResultItem.supplier || 'Supplier',
        monthSold: searchResultItem.monthSold || 0,
        moq: searchResultItem.moq || searchResultItem.minOrderQuantity || 1,
        rating: searchResultItem.sellerRating || searchResultItem.rating || null,
      };
    }
  }, [calculateINRPrice]);

  // Compress image
  const compressImage = useCallback(async (blob) => {
    return new Promise((resolve) => {
      const img = new Image();
      const url = URL.createObjectURL(blob);
      img.onload = () => {
        URL.revokeObjectURL(url);
        let { width, height } = img;
        const maxDim = 400;
        if (width > maxDim || height > maxDim) {
          const scale = maxDim / Math.max(width, height);
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        canvas.toBlob((b) => resolve(b || blob), 'image/jpeg', 0.7);
      };
      img.onerror = () => { URL.revokeObjectURL(url); resolve(blob); };
      img.src = url;
    });
  }, []);

  // Find similar products with pagination - using minimum variant prices
  const handleFindSimilar = useCallback(async (page = 1) => {
    // Use the currently displayed image (selected thumbnail), not the default mainImage
    const allImages = data?.images?.length ? data.images : (data?.mainImage ? [data.mainImage] : []);
    const imageUrl = allImages[activeImageIdx] || allImages[0];
    if (!imageUrl) { alert('No image available'); return; }

    // Clear selected product to show the grid
    setSelectedProductDetails(null);
    setSelectedProductVariants([]);
    setLoadingSimilar(true);
    setFindSimilarProgress(10);
    setFindSimilarStatus('Preparing image...');
    setShowSimilar(true);
    setCurrentPage(page);

    try {
      setFindSimilarProgress(25);
      setFindSimilarStatus('Fetching source image...');
      const proxyUrl = `${BACKEND_API.replace(/\/+$/, '')}/proxy/image?url=${encodeURIComponent(imageUrl)}`;
      const imgResponse = await fetch(proxyUrl);
      if (!imgResponse.ok) throw new Error('Failed to fetch image');

      let imgBlob = await imgResponse.blob();
      imgBlob = await compressImage(imgBlob);
      setFindSimilarProgress(45);
      setFindSimilarStatus('Compressing image...');

      const base64Image = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(imgBlob);
      });

      const baseUrl = process.env.REACT_APP_APIUrl1688;
      const token = await fetchToken();
      setFindSimilarProgress(60);
      setFindSimilarStatus('Searching similar products...');
      const response = await fetch(baseUrl + '/get-product-info-by-image', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-API-Token': token },
        body: JSON.stringify({ imageBase64: base64Image, beginPage: page, pageSize: 6, country: 'en' }),
      });

      if (!response.ok) throw new Error('API error');
      const result = await response.json();

      // Set total pages from response
      const totalPagesFromApi = result.pagination?.totalPage || result.totalPages || 5;
      setTotalPages(totalPagesFromApi);

      let productsData = result.products || result.result?.result?.data || result.data || [];
      if (Array.isArray(productsData) && productsData.length > 0) {
        setFindSimilarProgress(75);
        setFindSimilarStatus('Fetching product details and variants...');
        
        // Fetch full product details for each search result to get minimum variant prices
        const processed = await Promise.all(
          productsData.slice(0, 6).map((item) => fetchProductDetailsWithVariants(item))
        );
        
        setFindSimilarProgress(100);
        setFindSimilarStatus('Similar products ready');
        setSimilarProducts(processed);
      } else {
        setFindSimilarProgress(100);
        setFindSimilarStatus('No similar products found');
      }
    } catch (err) {
      console.error('Find similar error:', err);
    } finally {
      setLoadingSimilar(false);
      setTimeout(() => {
        setFindSimilarProgress(0);
        setFindSimilarStatus('');
      }, 400);
    }
  }, [data, activeImageIdx, compressImage, fetchProductDetailsWithVariants]);

  // Open product variant page - inline in sidebar
  const openProductVariants = useCallback(async (product) => {
    // Set selected product mode inline (no new tab)
    setSelectedProductDetails(null);
    setSelectedProductVariants([]);
    setLoadingSelectedProduct(true);
    setShowSimilar(false); // Hide similar products grid, show product details

    // Store selected product basic info
    const selectedInfo = {
      offerId: product.offerId,
      title: product.title,
      image: product.image,
      rmb: product.price.rmb,
      inr: product.price.inr,
    };

    try {
      const baseUrl = process.env.REACT_APP_APIUrl1688;
      const token = await fetchToken();
      const response = await fetch(baseUrl + '/product-detail-by-offerid', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Token': token
        },
        body: JSON.stringify({ offerId: product.offerId.toString(), country: 'en' })
      });

      if (!response.ok) throw new Error('Failed to fetch product details');

      const data = await response.json();
      const productData = data.result?.result || data.product || data.data || data;

      // Merge with basic info
      productData._selectedInfo = selectedInfo;
      setSelectedProductDetails(productData);

      // Extract variants/SKUs and shipping info
      let skuInfos = productData.productSkuInfos || productData.skuInfos || productData.skuList ||
        productData.skus || productData.productInfo?.skuInfos || productData.data?.productSkuInfos ||
        productData.variants || [];

      // Get shipping details for variants (for weight/dimensions display only)
      const shippingDetails = productData.productShippingInfo?.skuShippingDetails || [];

      if (skuInfos.length > 0) {
        const processedVariants = await Promise.all(
          skuInfos.map(async (sku, idx) => {
            const skuId = sku.skuId || sku.id || sku.specId || `sku-${idx}`;
            const skuPrice = getOnePieceRmbPrice(sku, selectedInfo.rmb);

            // Find shipping info for this SKU (display only)
            const skuShipping = shippingDetails.find(s => s.skuId == skuId) || shippingDetails[0] || {};
            const weight = parseFloat(skuShipping.weight || 0);
            const length = parseFloat(skuShipping.length || 0);
            const width = parseFloat(skuShipping.width || 0);
            const height = parseFloat(skuShipping.height || 0);
            const dimensions = (length > 0 && width > 0 && height > 0) ? { length, width, height } : null;

            // Calculate price using the default pricing domain (display only)
            const priceData = await calculateINRPrice(skuPrice, product.offerId, skuId, weight, dimensions);

            let attributes = [];
            let attrImageUrl = null;
            if (sku.skuAttributes?.length > 0) {
              attributes = sku.skuAttributes.map(attr => {
                // Check for image URL in attribute
                if (!attrImageUrl && attr.skuImageUrl) {
                  attrImageUrl = attr.skuImageUrl;
                }
                return {
                  name: attr.attributeNameTrans || attr.attributeName || attr.name || 'Attribute',
                  value: attr.valueTrans || attr.value || attr.attributeValue || '',
                  imageUrl: attr.skuImageUrl || null
                };
              });
            } else if (sku.attributes?.length > 0) {
              attributes = sku.attributes.map(attr => {
                if (!attrImageUrl && (attr.skuImageUrl || attr.imageUrl)) {
                  attrImageUrl = attr.skuImageUrl || attr.imageUrl;
                }
                return {
                  name: attr.name || attr.attributeName || 'Attribute',
                  value: attr.value || attr.attributeValue || '',
                  imageUrl: attr.skuImageUrl || attr.imageUrl || null
                };
              });
            }

            // Get variant image - check multiple sources including attribute images
            const variantImage = attrImageUrl || sku.skuImage || sku.image || sku.imageUrl || sku.pic || sku.picUrl || selectedInfo.image;

            return {
              id: skuId,
              skuId: skuId,
              image: variantImage,
              price: {
                rmb: skuPrice,
                inr: priceData.inr || 0,
                breakdown: priceData.breakdown
              },
              stock: sku.amountOnSale || sku.stock || sku.canBookCount || 0,
              attributes: attributes,
              attributeText: attributes.map(a => a.value).filter(v => v).join(' / ') || sku.specAttrsText || `Variant ${idx + 1}`,
              // Weight and dimensions info (display only)
              weight: weight,
              dimensions: priceData.dimensions || dimensions,
              volumetricWeight: priceData.volumetricWeight || 0,
            };
          })
        );
        setSelectedProductVariants(processedVariants);
      }
    } catch (err) {
      console.error('Failed to fetch selected product details:', err);
      // Still show basic info even if detailed fetch fails
      setSelectedProductDetails({ _selectedInfo: selectedInfo });
    } finally {
      setLoadingSelectedProduct(false);
    }
  }, [calculateINRPrice]);

  // Go back to similar products grid
  const backToSimilarProducts = () => {
    setSelectedProductDetails(null);
    setSelectedProductVariants([]);
    setShowSimilar(true);
  };

  // Fetch product data function
  const fetchProductData = useCallback(async () => {
    if (!asinFromQuery) { setError("Missing ASIN"); setLoading(false); return; }
    setLoading(true);
    setError("");

    try {
      const token = localStorage.getItem('LOGIN_HEADER');
      const response = await fetch(`${BACKEND_API}order/amazon-product/${asinFromQuery}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const res = await response.json();
      if (res?.success && res?.data) {
        setData(res.data);
        if (res.data.error) setError(res.data.error);
      } else {
        setError(res?.message || "Failed to fetch product details");
      }
    } catch (err) {
      setError(err?.message || "Failed to fetch product details");
    } finally {
      setLoading(false);
    }
  }, [asinFromQuery]);

  // Fetch failed order amount for this ASIN
  const fetchFailedOrderAmount = useCallback(async () => {
    if (!asinFromQuery) return;
    try {
      const token = localStorage.getItem('LOGIN_HEADER');
      const response = await fetch(`${BACKEND_API}order/failed-amounts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ asins: [asinFromQuery] })
      });
      const result = await response.json();
      console.log('Failed order amounts response:', result);
      if (result.success && result.data && result.data[asinFromQuery]) {
        setFailedOrderAmount(result.data[asinFromQuery]);
      }
    } catch (err) {
      console.error('Failed to fetch order amount:', err);
    }
  }, [asinFromQuery]);

  // Fetch selected product details and variants
  const fetchSelectedProductDetails = useCallback(async () => {
    if (!isSelectedProductMode || !selectedOfferId) return;

    // Prevent duplicate fetches
    if (hasFetchedSelectedProduct.current) return;
    hasFetchedSelectedProduct.current = true;

    setLoadingSelectedProduct(true);
    try {
      const baseUrl = process.env.REACT_APP_APIUrl1688;
      const token = await fetchToken();
      const response = await fetch(baseUrl + '/product-detail-by-offerid', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Token': token
        },
        body: JSON.stringify({ offerId: selectedOfferId.toString(), country: 'en' })
      });

      if (!response.ok) throw new Error('Failed to fetch product details');

      const data = await response.json();
      console.log('Product detail API response:', data);

      const productData = data.result?.result || data.product || data.data || data;
      setSelectedProductDetails(productData);

      // Extract variants/SKUs
      let skuInfos = productData.productSkuInfos || productData.skuInfos || productData.skuList ||
        productData.skus || productData.productInfo?.skuInfos || productData.data?.productSkuInfos ||
        productData.variants || [];

      if (skuInfos.length > 0) {
        const processedVariants = await Promise.all(
          skuInfos.map(async (sku, idx) => {
            const skuPrice = getOnePieceRmbPrice(sku, selectedRmb);
            const priceData = await calculateINRPrice(skuPrice, selectedOfferId);

            let attributes = [];
            let attrImageUrl = null;
            if (sku.skuAttributes?.length > 0) {
              attributes = sku.skuAttributes.map(attr => {
                // Check for image URL in attribute
                if (!attrImageUrl && attr.skuImageUrl) {
                  attrImageUrl = attr.skuImageUrl;
                }
                return {
                  name: attr.attributeNameTrans || attr.attributeName || attr.name || 'Attribute',
                  value: attr.valueTrans || attr.value || attr.attributeValue || '',
                  imageUrl: attr.skuImageUrl || null
                };
              });
            } else if (sku.attributes?.length > 0) {
              attributes = sku.attributes.map(attr => {
                if (!attrImageUrl && (attr.skuImageUrl || attr.imageUrl)) {
                  attrImageUrl = attr.skuImageUrl || attr.imageUrl;
                }
                return {
                  name: attr.name || attr.attributeName || 'Attribute',
                  value: attr.value || attr.attributeValue || '',
                  imageUrl: attr.skuImageUrl || attr.imageUrl || null
                };
              });
            }

            // Get variant image - check multiple sources including attribute images
            const variantImage = attrImageUrl || sku.skuImage || sku.image || sku.imageUrl || sku.pic || sku.picUrl || selectedImage;

            return {
              id: sku.skuId || sku.id || sku.specId || `sku-${idx}`,
              skuId: sku.skuId || sku.id || sku.specId || `sku-${idx}`,
              image: variantImage,
              price: { rmb: skuPrice, inr: priceData.inr || 0 },
              stock: sku.amountOnSale || sku.stock || sku.canBookCount || 0,
              attributes: attributes,
              attributeText: attributes.map(a => a.value).filter(v => v).join(' / ') || sku.specAttrsText || `Variant ${idx + 1}`
            };
          })
        );
        setSelectedProductVariants(processedVariants);
      }
    } catch (err) {
      console.error('Failed to fetch selected product details:', err);
    } finally {
      setLoadingSelectedProduct(false);
    }
  }, [isSelectedProductMode, selectedOfferId, selectedRmb, selectedImage, calculateINRPrice]);

  // Open confirmation modal before mapping
  const openConfirmModal = (variant) => {
    setConfirmVariant(variant);
    setMappingRemark("");
    setSelectedHsnCategory('');
    setSelectedHsnMapping(null);
    setShowConfirmModal(true);
  };

  // Map variant to ASIN (called after confirmation) - Background process
  const handleMapVariant = async () => {
    const variant = confirmVariant;
    const offerId = selectedProductDetails?._selectedInfo?.offerId;
    const remark = mappingRemark;
    const asin = asinFromQuery;
    const productTitle = selectedProductDetails?.subjectTrans || selectedProductDetails?._selectedInfo?.title;
    const productImage = variant.image || selectedProductDetails?._selectedInfo?.image;

    if (!asin || !offerId) {
      // Store error result in sessionStorage for global notification provider
      sessionStorage.setItem('mappingResult', JSON.stringify({ success: false, message: 'Missing ASIN or Product ID' }));
      setShowConfirmModal(false);
      return;
    }

    const token = localStorage.getItem('LOGIN_HEADER');
    if (!token) {
      // Store error result in sessionStorage for global notification provider
      sessionStorage.setItem('mappingResult', JSON.stringify({ success: false, message: 'Please login to map products' }));
      setShowConfirmModal(false);
      return;
    }

    // Close confirmation modal immediately and show processing indicator
    setShowConfirmModal(false);
    setConfirmVariant(null);
    setIsMapping(true);

    // Store mapping info for background process tracking
    const mappingInfo = {
      asin,
      offerId,
      skuId: variant.skuId,
      variantText: variant.attributeText,
      startTime: Date.now()
    };

    // Save to sessionStorage so we can show result even after navigation
    sessionStorage.setItem('pendingMapping', JSON.stringify(mappingInfo));

    // Prepare HSN info if selected (matching hsn_mappings collection structure)
    const hsnInfo = selectedHsnMapping ? {
      hsnMappingId: selectedHsnMapping._id,
      category: selectedHsnMapping.category || selectedHsnMapping.Item,
      Item: selectedHsnMapping.Item || selectedHsnMapping.category,
      hsnCode: selectedHsnMapping.hsnCode || String(selectedHsnMapping.HSN),
      HSN: selectedHsnMapping.HSN || selectedHsnMapping.hsnCode,
      BCD: selectedHsnMapping.BCD || selectedHsnMapping.bcd,
      Surcharge: selectedHsnMapping.Surcharge || selectedHsnMapping.surcharge,
      FinalCustomDuty: selectedHsnMapping.FinalCustomDuty,
      IGST: selectedHsnMapping.IGST,
      gstRate: selectedHsnMapping.gstRate,
      igstRate: selectedHsnMapping.igstRate,
      customsDutyRate: selectedHsnMapping.customsDutyRate,
    } : null;

    // Run mapping in background
    try {
      const response = await fetch(`${BACKEND_API}order/map-asin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          asin: asin,
          offerId: offerId.toString(),
          skuId: variant.skuId || null,
          productTitle: productTitle,
          productImage: productImage,
          productPrice: variant.price.rmb,
          variantAttributes: variant.attributeText,
          remark: remark || null,
          hsnInfo: hsnInfo,
        }),
      });

      const data = await response.json();

      // Clear pending mapping
      sessionStorage.removeItem('pendingMapping');

      const result = response.ok && data.success ? {
        success: true,
        message: data.message,
        asin: asin,
        skuId: variant.skuId,
        offerId: data.data?.offerId,
        productSku: data.data?.productSku,
        convertedOrders: data.data?.convertedOrders || 0,
        convertedOrderIds: data.data?.convertedOrderIds || [],
        convertedOrderDetails: data.data?.convertedOrderDetails || [],
      } : { success: false, message: data.message || 'Failed to map variant' };

      // Store result in sessionStorage for global notification provider
      sessionStorage.setItem('mappingResult', JSON.stringify(result));

    } catch (error) {
      console.error('Map variant failed:', error);
      sessionStorage.removeItem('pendingMapping');

      const result = { success: false, message: 'Failed to map variant: ' + error.message };
      sessionStorage.setItem('mappingResult', JSON.stringify(result));
    } finally {
      setIsMapping(false);
    }
  };

  useEffect(() => {
    fetchProductData();
    fetchFailedOrderAmount();
  }, [fetchProductData, fetchFailedOrderAmount]);

  useEffect(() => {
    if (isSelectedProductMode) {
      fetchSelectedProductDetails();
    }
  }, [isSelectedProductMode, fetchSelectedProductDetails]);

  // Reload/Retry function
  const handleReload = () => {
    setData(null);
    setError("");
    fetchProductData();
  };

  const images = useMemo(() => {
    if (!data?.images?.length) return data?.mainImage ? [data.mainImage] : [];
    return data.images;
  }, [data]);

  const amazonUrl = `https://www.amazon.in/dp/${asinFromQuery}`;

  // Extract weight and dimensions from product details
  const getProductSpec = (key) => {
    const details = { ...data?.productDetails, ...data?.technicalDetails, ...data?.additionalInfo };
    for (const [k, v] of Object.entries(details)) {
      if (k.toLowerCase().includes(key.toLowerCase())) return v;
    }
    return null;
  };

  if (loading) {
    return (
      <div className="apd-container apd-loading-container">
        <div className="apd-loading">
          <FaSpinner className="apd-spinner" />
          <p>Searching for product details...</p>
          <span className="apd-asin-badge">ASIN: {asinFromQuery}</span>
        </div>
      </div>
    );
  }

  if (!data && error) {
    return (
      <div className="apd-container apd-loading-container">
        <div className="apd-error">
          <h4>Unable to load product</h4>
          <p>{error}</p>
          <div className="apd-error-actions">
            <button className="apd-btn-reload" onClick={handleReload}>
              <FaSync size={12} /> Retry Scraping
            </button>
            <a href={amazonUrl} target="_blank" rel="noopener noreferrer" className="apd-btn-amazon">
              <FaExternalLinkAlt size={12} /> View on Amazon
            </a>
          </div>
        </div>
      </div>
    );
  }


  return (
    <div className={`apd-container ${showSimilar ? 'apd-with-sidebar' : ''}`}>
      {/* Background Mapping Indicator - Fixed Position */}
      {isMapping && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          backgroundColor: '#1e293b',
          color: '#fff',
          padding: '12px 20px',
          borderRadius: '8px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          zIndex: 9998,
          fontSize: '13px',
          fontWeight: 500
        }}>
          <FaSpinner className="apd-spinner" style={{ width: '16px', height: '16px' }} />
          <span>Mapping ASIN in background...</span>
        </div>
      )}

      {/* Main Content */}
      <div className="apd-main">
        {/* Header */}
        <div className="apd-header">
          <div className="apd-header-left">
            <span className="apd-amazon-badge"><i className="fab fa-amazon"></i> Amazon.in</span>
            <span className="apd-asin-badge">ASIN: {asinFromQuery}</span>
          </div>
          <div className="apd-header-right">
            <button className="apd-btn-reload" onClick={handleReload} title="Reload product data">
              <FaSync size={12} /> Reload
            </button>
            <a href={amazonUrl} target="_blank" rel="noopener noreferrer" className="apd-btn-amazon">
              <FaExternalLinkAlt size={12} /> View on Amazon
            </a>
          </div>
        </div>

        {/* Product Layout */}
        <div className="apd-product-layout">
          {/* Images Section */}
          <div className="apd-images-section">
            <div className="apd-main-image">
              {images.length > 0 ? (
                <>
                  <img src={images[activeImageIdx] || images[0]} alt={data?.title || "Product"} onError={(e) => { e.currentTarget.src = noImage; }} />
                  <button className="apd-find-similar-icon" onClick={() => handleFindSimilar(1)} disabled={loadingSimilar} title="Find Similar Products">
                    {loadingSimilar ? <FaSpinner className="apd-spinner" /> : <TbZoomScan size={20} />}
                  </button>
                </>
              ) : (
                <div className="apd-no-image">No Image</div>
              )}
            </div>
            {images.length > 1 && (
              <div className="apd-thumbnails">
                {images.slice(0, 8).map((img, idx) => (
                  <img key={idx} src={img} alt={`Thumb ${idx + 1}`} className={idx === activeImageIdx ? "active" : ""} onClick={() => setActiveImageIdx(idx)} onError={(e) => { e.currentTarget.style.display = "none"; }} />
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="apd-info-section">
            <h1 className="apd-title">{data?.title || "Product title not available"}</h1>

            {data?.brand && (
              <div className="apd-brand">
                <FaStore size={12} /> <span>{data.brand}</span> Store
              </div>
            )}

            {/* Rating */}
            {data?.rating && (
              <div className="apd-rating">
                <FaStar className="apd-star" />
                <span className="apd-rating-value">{data.rating}</span>
                {data.ratingCount && <span className="apd-rating-count">({data.ratingCount})</span>}
              </div>
            )}

            {/* Price Section */}
            <div className="apd-price-box">
              {data?.discount && !data.discount.includes("{") && (
                <span className="apd-discount">{data.discount}</span>
              )}
              <div className="apd-price">{data?.price || "Price not available"}</div>
              {data?.mrp && data.mrp !== data.price && (
                <div className="apd-mrp">M.R.P.: <s>{data.mrp}</s></div>
              )}
              <div className="apd-tax">Inclusive of all taxes</div>
            </div>

            {/* Specs Grid */}
            <div className="apd-specs-grid">
              <div className="apd-spec-item">
                <FaBox className="apd-spec-icon" />
                <div className="apd-spec-content">
                  <span className="apd-spec-label">SKU/Model</span>
                  <span className="apd-spec-value">{getProductSpec('model') || getProductSpec('sku') || asinFromQuery}</span>
                </div>
              </div>
              <div className="apd-spec-item">
                <FaWeight className="apd-spec-icon" />
                <div className="apd-spec-content">
                  <span className="apd-spec-label">Weight</span>
                  <span className="apd-spec-value">{getProductSpec('weight') || 'N/A'}</span>
                </div>
              </div>
              <div className="apd-spec-item">
                <FaRuler className="apd-spec-icon" />
                <div className="apd-spec-content">
                  <span className="apd-spec-label">Dimensions</span>
                  <span className="apd-spec-value">{getProductSpec('dimension') || getProductSpec('size') || 'N/A'}</span>
                </div>
              </div>
              <div className="apd-spec-item">
                <FaStore className="apd-spec-icon" />
                <div className="apd-spec-content">
                  <span className="apd-spec-label">Seller</span>
                  <span className="apd-spec-value">{data?.seller || 'N/A'}</span>
                </div>
              </div>
            </div>

            {/* Seller Info */}
            {data?.seller && (
              <div className="apd-seller-box">
                <span className="apd-seller-label">Sold by:</span>
                <span className="apd-seller-name">{data.seller}</span>
                {data?.sellerRating && <span className="apd-seller-rating">{data.sellerRating}</span>}
                {data?.fulfilledBy && <span className="apd-fulfilled">{data.fulfilledBy}</span>}
              </div>
            )}
          </div>
        </div>

        {/* Tabs Section */}
        <div className="apd-tabs-section">
          <div className="apd-tabs">
            <button className={activeTab === "description" ? "active" : ""} onClick={() => setActiveTab("description")}>Description</button>
            <button className={activeTab === "details" ? "active" : ""} onClick={() => setActiveTab("details")}>Product Details</button>
            <button className={activeTab === "technical" ? "active" : ""} onClick={() => setActiveTab("technical")}>Technical Details</button>
            <button className={activeTab === "reviews" ? "active" : ""} onClick={() => setActiveTab("reviews")}>Reviews</button>
          </div>

          <div className="apd-tab-content">
            {activeTab === "description" && (
              <div className="apd-description">
                {data?.features?.length > 0 ? (
                  <ul className="apd-features-list">
                    {data.features.map((f, i) => <li key={i}>{f}</li>)}
                  </ul>
                ) : data?.description ? (
                  <div className="apd-desc-content">
                    {data.description.split(/[.!?]+/).filter(s => s.trim()).map((sentence, i) => (
                      <p key={i}>{sentence.trim()}.</p>
                    ))}
                  </div>
                ) : <p className="apd-no-data">No description available</p>}
              </div>
            )}

            {activeTab === "details" && (
              <div className="apd-details-table">
                {data?.productDetails && Object.keys(data.productDetails).length > 0 ? (
                  <table><tbody>
                    {Object.entries(data.productDetails).map(([k, v], i) => (
                      <tr key={i}><th>{k}</th><td>{v}</td></tr>
                    ))}
                  </tbody></table>
                ) : <p className="apd-no-data">No product details available</p>}
              </div>
            )}

            {activeTab === "technical" && (
              <div className="apd-details-table">
                {data?.technicalDetails && Object.keys(data.technicalDetails).length > 0 ? (
                  <table><tbody>
                    {Object.entries(data.technicalDetails).map(([k, v], i) => (
                      <tr key={i}><th>{k}</th><td>{v}</td></tr>
                    ))}
                  </tbody></table>
                ) : <p className="apd-no-data">No technical details available</p>}
              </div>
            )}

            {activeTab === "reviews" && (
              <div className="apd-reviews">
                {data?.topReviews?.length > 0 ? (
                  data.topReviews.map((review, idx) => (
                    <div key={idx} className="apd-review-item">
                      <div className="apd-review-header">
                        <span className="apd-review-author">{review.author}</span>
                        {review.verified && <span className="apd-verified">Verified</span>}
                        <span className="apd-review-rating">{review.rating}</span>
                      </div>
                      {review.title && <div className="apd-review-title">{review.title}</div>}
                      <p className="apd-review-text">{review.text}</p>
                    </div>
                  ))
                ) : <p className="apd-no-data">No reviews available</p>}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Similar Products Sidebar OR Selected Product Details */}
      {(showSimilar || selectedProductDetails) && (
        <div className="apd-sidebar">
          {selectedProductDetails ? (
            <>
              {/* Selected Product Detail View - Inline */}
              <div className="apd-sidebar-header">
                <button
                  onClick={backToSimilarProducts}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    background: 'none',
                    border: 'none',
                    color: '#3b82f6',
                    fontSize: '13px',
                    fontWeight: 500,
                    cursor: 'pointer',
                    padding: 0
                  }}
                >
                  ← Back to Similar Products
                </button>
                <button
                  className="apd-sidebar-close"
                  onClick={backToSimilarProducts}
                  title="Back to similar products"
                >×</button>
              </div>

              {loadingSelectedProduct ? (
                <div className="apd-sidebar-loading">
                  <FaSpinner className="apd-spinner" />
                  <p>Loading product details...</p>
                </div>
              ) : (
                <div className="apd-detail-scroll">
                  {/* Two Column Layout - Image Left, Info Right */}
                  <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: '16px', padding: '16px', borderBottom: '1px solid #e2e8f0' }}>
                    {/* Left - Image & Thumbnails */}
                    <div>
                      <div style={{ position: 'relative', marginBottom: '8px' }}>
                        {(() => {
                          const productImages = selectedProductDetails.productImage?.images ||
                            selectedProductDetails.images ||
                            selectedProductDetails.imageList ||
                            selectedProductDetails.productInfo?.images ||
                            [];
                          const selectedInfo = selectedProductDetails._selectedInfo || {};
                          // If a variant is selected, show the variant's image; otherwise show product image
                          const selectedVariant = selectedProductVariants[selectedVariantImageIdx];
                          const mainImg = selectedVariant?.image || productImages[0] || selectedInfo.image || noImage;
                          return (
                            <img
                              src={mainImg}
                              alt={selectedProductDetails.subjectTrans || selectedInfo.title || 'Product'}
                              style={{ width: '100%', aspectRatio: '1/1', objectFit: 'contain', borderRadius: '8px', border: '1px solid #e5e7eb', backgroundColor: '#f8fafc' }}
                              onError={(e) => { e.currentTarget.src = noImage; }}
                            />
                          );
                        })()}
                      </div>
                      {/* Thumbnails - Show variant images */}
                      {(() => {
                        // Get unique variant images for thumbnails
                        const variantImages = selectedProductVariants
                          .map(v => v.image)
                          .filter((img, idx, arr) => img && arr.indexOf(img) === idx);

                        // Fallback to product images if no variant images
                        const productImages = selectedProductDetails.productImage?.images ||
                          selectedProductDetails.images ||
                          selectedProductDetails.imageList ||
                          selectedProductDetails.productInfo?.images ||
                          [];

                        const thumbnailImages = variantImages.length > 0 ? variantImages : productImages;

                        return thumbnailImages.length > 1 && (
                          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                            {thumbnailImages.slice(0, 5).map((img, idx) => {
                              // Find the variant index for this image
                              const variantIdx = selectedProductVariants.findIndex(v => v.image === img);
                              const isActive = variantIdx >= 0
                                ? variantIdx === selectedVariantImageIdx
                                : idx === selectedVariantImageIdx;

                              return (
                                <img
                                  key={idx}
                                  src={img}
                                  alt={`Thumb ${idx + 1}`}
                                  style={{
                                    width: '36px',
                                    height: '36px',
                                    objectFit: 'cover',
                                    borderRadius: '4px',
                                    border: isActive ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                                    cursor: 'pointer'
                                  }}
                                  onClick={() => {
                                    if (variantIdx >= 0) {
                                      setSelectedVariantImageIdx(variantIdx);
                                    } else {
                                      setSelectedVariantImageIdx(idx);
                                    }
                                  }}
                                  onError={(e) => { e.currentTarget.src = noImage; }}
                                />
                              );
                            })}
                          </div>
                        );
                      })()}
                    </div>

                    {/* Right - Product Info */}
                    <div>
                      {/* Store Badge */}
                      {/* <div style={{ marginBottom: '8px' }}>
                        <span style={{
                          backgroundColor: '#ff6600',
                          color: '#fff',
                          padding: '3px 8px',
                          borderRadius: '4px',
                          fontSize: '10px',
                          fontWeight: 600
                        }}>
                          {selectedProductDetails.companyName || selectedProductDetails.sellerInfo?.companyName || '1688 Supplier'}
                        </span>
                      </div> */}

                      {/* Title */}
                      <h2 style={{ fontSize: '14px', fontWeight: 500, color: '#1e293b', marginBottom: '12px', lineHeight: 1.4 }}>
                        {selectedProductDetails.subjectTrans || selectedProductDetails.subject || selectedProductDetails._selectedInfo?.title || 'Product'}
                      </h2>

                      {/* Price */}
                      <div style={{ marginBottom: '12px' }}>
                        <div style={{ fontSize: '10px', color: '#64748b', marginBottom: '2px' }}>Estimated Landed Price</div>
                        <div style={{ fontSize: '28px', fontWeight: 700, color: '#1e293b' }}>
                          ₹{selectedProductVariants[selectedVariantImageIdx]?.price?.inr?.toFixed(2) || '0.00'}
                        </div>
                        <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
                          MOQ: {selectedProductDetails.productSaleInfo?.minOrderQuantity || selectedProductDetails.moq || 1} pieces
                        </div>
                      </div>

                      {/* Info Grid */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                        <div style={{ padding: '8px', backgroundColor: '#f8fafc', borderRadius: '6px' }}>
                          <div style={{ fontSize: '9px', color: '#64748b', textTransform: 'uppercase' }}>Offer ID</div>
                          <div style={{ fontSize: '12px', fontWeight: 500, color: '#1e293b' }}>{selectedProductDetails._selectedInfo?.offerId || 'N/A'}</div>
                        </div>
                        <div style={{ padding: '8px', backgroundColor: '#f8fafc', borderRadius: '6px' }}>
                          <div style={{ fontSize: '9px', color: '#64748b', textTransform: 'uppercase' }}>Unit</div>
                          <div style={{ fontSize: '12px', fontWeight: 500, color: '#1e293b' }}>
                            {selectedProductDetails.productSaleInfo?.unit || 'piece'}
                          </div>
                        </div>
                        {selectedProductDetails.productSaleInfo?.amountOnSale && (
                          <div style={{ padding: '8px', backgroundColor: '#f8fafc', borderRadius: '6px' }}>
                            <div style={{ fontSize: '9px', color: '#64748b', textTransform: 'uppercase' }}>Stock</div>
                            <div style={{ fontSize: '12px', fontWeight: 500, color: '#1e293b' }}>
                              {selectedProductDetails.productSaleInfo.amountOnSale}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Price Margin Analysis Section - Shows Landed Cost Breakdown */}
                  {failedOrderAmount && failedOrderAmount.totalAmount > 0 && (
                    (() => {
                      const currentVariant = selectedProductVariants[selectedVariantImageIdx];
                      const currentVariantPrice = currentVariant?.price?.inr || 0;
                      const breakdown = currentVariant?.price?.breakdown || {};
                      const orderCount = failedOrderAmount.orderCount;
                      const avgOrderValue = failedOrderAmount.totalAmount / orderCount;
                      const adjustedOrderValue = avgOrderValue * 0.7; // Apply 30% reduction
                      const marginPerOrder = adjustedOrderValue - currentVariantPrice;
                      const totalMargin = marginPerOrder * orderCount;
                      const marginPercent = adjustedOrderValue > 0 ? ((marginPerOrder / adjustedOrderValue) * 100).toFixed(1) : 0;
                      const isPositive = marginPerOrder > 0;

                      return (
                        <div style={{ padding: '16px', borderBottom: '1px solid #e2e8f0' }}>
                          <div style={{ fontSize: '12px', fontWeight: 600, color: '#1e293b', marginBottom: '12px' }}>
                            Price Margin Analysis
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '12px' }}>
                            <div>
                              <div style={{ fontSize: '10px', color: '#64748b', marginBottom: '4px' }}>Order Value (-30%)</div>
                              <div style={{ fontSize: '18px', fontWeight: 700, color: '#3b82f6' }}>₹{adjustedOrderValue.toFixed(2)}</div>
                            </div>
                            <div>
                              <div style={{ fontSize: '10px', color: '#64748b', marginBottom: '4px' }}>Source Price</div>
                              <div style={{ fontSize: '18px', fontWeight: 700, color: '#1e293b' }}>₹{currentVariantPrice.toFixed(2)}</div>
                            </div>
                            <div>
                              <div style={{ fontSize: '10px', color: '#64748b', marginBottom: '4px' }}>Profit</div>
                              <div style={{ fontSize: '18px', fontWeight: 700, color: isPositive ? '#16a34a' : '#dc2626' }}>
                                ₹{totalMargin.toFixed(2)} <span style={{ fontSize: '14px' }}>{isPositive ? '+' : ''}{marginPercent}%</span>
                              </div>
                            </div>
                            <div>
                              <div style={{ fontSize: '10px', color: '#64748b', marginBottom: '4px' }}>Order Qty</div>
                              <div style={{ fontSize: '18px', fontWeight: 700, color: '#1e293b' }}>{orderCount}</div>
                            </div>
                          </div>


                        </div>
                      );
                    })()
                  )}

                  {/* Failed Order Demand Section */}
                  {failedOrderAmount && failedOrderAmount.totalAmount > 0 && (
                    <div style={{
                      margin: '16px',
                      padding: '12px 16px',
                      backgroundColor: '#fef3c7',
                      borderRadius: '8px',
                      border: '1px solid #fcd34d'
                    }}>
                      <div style={{ fontSize: '11px', color: '#92400e', marginBottom: '4px' }}>
                        Failed Order Demand for ASIN
                      </div>
                      <div style={{ fontSize: '20px', fontWeight: 700, color: '#92400e' }}>
                        ₹{failedOrderAmount.totalAmount.toLocaleString('en-IN')} ({failedOrderAmount.orderCount} orders)
                      </div>
                    </div>
                  )}

                  {/* Available Variants Section */}
                  <div style={{ padding: '0 16px 16px' }}>
                    <h4 style={{
                      fontSize: '14px',
                      fontWeight: 600,
                      color: '#1e293b',
                      marginBottom: '12px',
                      paddingBottom: '8px',
                      borderBottom: '1px solid #e2e8f0'
                    }}>
                      Available Variants ({selectedProductVariants.length})
                    </h4>

                    {selectedProductVariants.length > 0 ? (
                      <div className="apd-variants-scroll">
                        {selectedProductVariants.map((variant, idx) => (
                          <div
                            key={variant.id}
                            onClick={() => setSelectedVariantImageIdx(idx)}
                            style={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: '12px',
                              padding: '10px 12px',
                              backgroundColor: idx === selectedVariantImageIdx ? '#eff6ff' : '#fff',
                              border: idx === selectedVariantImageIdx ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                              borderRadius: '8px',
                              cursor: 'pointer'
                            }}
                          >
                            <img
                              src={variant.image || selectedProductDetails._selectedInfo?.image || noImage}
                              alt={variant.attributeText}
                              style={{ width: '48px', height: '48px', objectFit: 'cover', borderRadius: '6px', flexShrink: 0 }}
                              onError={(e) => { e.currentTarget.src = noImage; }}
                            />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: '12px', fontWeight: 500, color: '#1e293b', marginBottom: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {variant.attributeText}
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                <span style={{ fontSize: '15px', fontWeight: 600, color: '#ea580c' }}>
                                  ₹{variant.price.inr.toFixed(0)}
                                </span>
                              </div>
                              {/* Weight & Dimensions Row */}
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                {variant.weight > 0 && (
                                  <span style={{ fontSize: '9px', backgroundColor: '#fef3c7', color: '#92400e', padding: '2px 5px', borderRadius: '3px', display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
                                    <FaWeight size={8} />
                                    {variant.weight.toFixed(2)}kg
                                  </span>
                                )}
                                {variant.dimensions && variant.dimensions.length > 0 && (
                                  <span style={{ fontSize: '9px', backgroundColor: '#e0e7ff', color: '#3730a3', padding: '2px 5px', borderRadius: '3px', display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
                                    <FaRuler size={8} />
                                    {variant.dimensions.length}×{variant.dimensions.width}×{variant.dimensions.height}cm
                                  </span>
                                )}
                                {variant.stock > 0 && (
                                  <span style={{ fontSize: '9px', color: '#10b981', backgroundColor: '#dcfce7', padding: '2px 5px', borderRadius: '3px' }}>
                                    Stock: {variant.stock}
                                  </span>
                                )}
                              </div>
                            </div>
                            {!isFromResearch && <button
                              onClick={(e) => {
                                e.stopPropagation();
                                openConfirmModal(variant);
                              }}
                              disabled={isMapping}
                              style={{
                                padding: '8px 16px',
                                backgroundColor: isMapping ? '#9ca3af' : '#3b82f6',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '6px',
                                fontSize: '12px',
                                fontWeight: 600,
                                cursor: isMapping ? 'not-allowed' : 'pointer',
                                flexShrink: 0,
                                alignSelf: 'center'
                              }}
                            >
                              Map
                            </button>}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ padding: '20px', textAlign: 'center', color: '#64748b', fontSize: '13px', backgroundColor: '#f8fafc', borderRadius: '8px' }}>
                        No variant information available
                      </div>
                    )}
                  </div>

                  {/* ===== Product Detail Sections (similar to admin panel) ===== */}

                  {/* Product Sale Info */}
                  <div className="alibaba-section" style={{ marginLeft: 0, marginRight: 0 }}>
                    <div className="alibaba-section-header">
                      <div className="alibaba-section-title">📊 Product Sale Info</div>
                    </div>
                    <div className="alibaba-section-content">
                      <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                          <tbody>
                            {Object.entries(selectedProductDetails.productSaleInfo || {})
                              .filter(([key]) => key !== 'priceRangeList')
                              .map(([key, value], idx) => {
                                let displayValue = '';
                                if (Array.isArray(value)) {
                                  displayValue = value.length > 0 ? value.join(', ') : '-';
                                } else if (typeof value === 'object' && value !== null) {
                                  displayValue = Object.entries(value).map(([k, v]) => `${k}: ${v}`).join(', ');
                                } else {
                                  displayValue = String(value);
                                }
                                return (
                                  <tr key={key} style={{ background: idx % 2 === 0 ? '#fff' : '#f8fafc' }}>
                                    <td style={{ padding: '8px 12px', borderBottom: '1px solid #e5e7eb', color: '#64748b', fontWeight: 500, textTransform: 'uppercase', fontSize: '10px', width: '140px' }}>
                                      {key}
                                    </td>
                                    <td style={{ padding: '8px 12px', borderBottom: '1px solid #e5e7eb', color: '#1e293b', fontWeight: 500 }}>
                                      {displayValue}
                                    </td>
                                  </tr>
                                );
                              })}
                          </tbody>
                        </table>
                      </div>
                      {(!selectedProductDetails.productSaleInfo || Object.keys(selectedProductDetails.productSaleInfo).length === 0) && (
                        <div style={{ color: '#94a3b8', fontSize: '12px', padding: '12px', textAlign: 'center' }}>No sale info available</div>
                      )}
                    </div>
                  </div>
                  <div className="alibaba-section" style={{ marginLeft: 0, marginRight: 0 }}>
                    <div className="alibaba-section-header">
                      <div className="alibaba-section-title">🚚 Shipping Info</div>
                    </div>
                    <div className="alibaba-section-content">
                      {(() => {
                        const shippingInfo = selectedProductDetails.productShippingInfo || {};
                        const skuShippingDetails = shippingInfo.skuShippingDetails || [];
                        const simpleFields = Object.entries(shippingInfo)
                          .filter(([key, value]) => key !== 'skuShippingDetails' && !Array.isArray(value));
                        return (
                          <>
                            {/* {simpleFields.length > 0 && (
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '10px', marginBottom: skuShippingDetails.length > 0 ? '16px' : 0 }}>
                                {simpleFields.map(([key, value]) => (
                                  <div key={key} style={{ padding: '10px', background: '#f0f9ff', borderRadius: '6px', border: '1px solid #bae6fd' }}>
                                    <div style={{ fontSize: '10px', color: '#0369a1', marginBottom: '4px', textTransform: 'uppercase' }}>{key}</div>
                                    <div style={{ fontSize: '12px', fontWeight: 600, color: '#0c4a6e' }}>
                                      {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )} */}
                            {skuShippingDetails.length > 0 && (
                              <div>
                                <div style={{ fontSize: '11px', fontWeight: 600, color: '#0369a1', marginBottom: '8px' }}>
                                  SKU Shipping Details ({skuShippingDetails.length} variants)
                                </div>
                                <div style={{ overflowX: 'auto', border: '1px solid #bae6fd', borderRadius: '8px' }}>
                                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                                    <thead>
                                      <tr style={{ background: '#f0f9ff' }}>
                                        <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid #bae6fd', color: '#0369a1', fontWeight: 600 }}>SKU ID</th>
                                        <th style={{ padding: '8px 10px', textAlign: 'center', borderBottom: '1px solid #bae6fd', color: '#0369a1', fontWeight: 600 }}>Weight (kg)</th>
                                        <th style={{ padding: '8px 10px', textAlign: 'center', borderBottom: '1px solid #bae6fd', color: '#0369a1', fontWeight: 600 }}>L × W × H (cm)</th>
                                        <th style={{ padding: '8px 10px', textAlign: 'center', borderBottom: '1px solid #bae6fd', color: '#0369a1', fontWeight: 600 }}>Spec ID</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {skuShippingDetails.slice(0, 20).map((sku, idx) => (
                                        <tr key={idx} style={{ background: idx % 2 === 0 ? '#fff' : '#f8fafc' }}>
                                          <td style={{ padding: '6px 10px', borderBottom: '1px solid #e5e7eb', fontFamily: 'monospace', color: '#1e293b' }}>{sku.skuId || '-'}</td>
                                          <td style={{ padding: '6px 10px', textAlign: 'center', borderBottom: '1px solid #e5e7eb', color: '#1e293b', fontWeight: 500 }}>{sku.weight || '-'}</td>
                                          <td style={{ padding: '6px 10px', textAlign: 'center', borderBottom: '1px solid #e5e7eb', color: '#1e293b' }}>
                                            {sku.length && sku.width && sku.height ? `${sku.length} × ${sku.width} × ${sku.height}` : '-'}
                                          </td>
                                          <td style={{ padding: '6px 10px', textAlign: 'center', borderBottom: '1px solid #e5e7eb', color: '#64748b', fontFamily: 'monospace', fontSize: '10px' }}>{sku.specId || '-'}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                  {skuShippingDetails.length > 20 && (
                                    <div style={{ padding: '8px', textAlign: 'center', background: '#f0f9ff', color: '#0369a1', fontSize: '11px' }}>
                                      ... and {skuShippingDetails.length - 20} more variants
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                            {simpleFields.length === 0 && skuShippingDetails.length === 0 && (
                              <div style={{ color: '#94a3b8', fontSize: '12px' }}>No shipping info available</div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Product Attributes */}
                  {(selectedProductDetails.productAttribute || []).length > 0 && (
                    <div className="alibaba-section" style={{ marginLeft: 0, marginRight: 0 }}>
                      <div className="alibaba-section-header">
                        <div className="alibaba-section-title">📋 Product Attributes ({(selectedProductDetails.productAttribute || []).length})</div>
                      </div>
                      <div className="alibaba-section-content">
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
                          {(selectedProductDetails.productAttribute || []).map((attr, idx) => (
                            <div key={idx} style={{ display: 'flex', padding: '8px 12px', background: '#f8fafc', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
                              <span style={{ fontSize: '11px', color: '#64748b', minWidth: '100px', flexShrink: 0 }}>
                                {attr.attributeNameTrans || attr.attributeName || attr.name || `Attr ${idx + 1}`}
                              </span>
                              <span style={{ fontSize: '11px', fontWeight: 500, color: '#1e293b', wordBreak: 'break-word' }}>
                                {attr.valueTrans || attr.value || attr.attributeValue || 'N/A'}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Tags */}
                  {(selectedProductDetails.tagInfoList || []).length > 0 && (
                    <div className="alibaba-section" style={{ marginLeft: 0, marginRight: 0 }}>
                      <div className="alibaba-section-header">
                        <div className="alibaba-section-title">🏷️ Tags ({(selectedProductDetails.tagInfoList || []).length})</div>
                      </div>
                      <div className="alibaba-section-content">
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                          {(selectedProductDetails.tagInfoList || []).map((tag, idx) => (
                            <span key={idx} style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '6px 12px',
                              borderRadius: '20px',
                              fontSize: '11px',
                              fontWeight: 500,
                              background: tag.value === true || tag.value === 'true' ? '#dcfce7' : '#f3f4f6',
                              color: tag.value === true || tag.value === 'true' ? '#166534' : '#374151',
                              border: `1px solid ${tag.value === true || tag.value === 'true' ? '#bbf7d0' : '#e5e7eb'}`
                            }}>
                              {tag.key}: {String(tag.value)}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Product Features */}
                  {selectedProductDetails.productFeature && Object.keys(selectedProductDetails.productFeature).length > 0 && (
                    <div className="alibaba-section" style={{ marginLeft: 0, marginRight: 0 }}>
                      <div className="alibaba-section-header">
                        <div className="alibaba-section-title">⭐ Product Features</div>
                      </div>
                      <div className="alibaba-section-content">
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '10px' }}>
                          {Object.entries(selectedProductDetails.productFeature || {}).map(([key, value]) => (
                            <div key={key} style={{ padding: '10px', background: '#fefce8', borderRadius: '6px', border: '1px solid #fef08a' }}>
                              <div style={{ fontSize: '10px', color: '#a16207', marginBottom: '4px', textTransform: 'uppercase' }}>{key}</div>
                              <div style={{ fontSize: '12px', fontWeight: 600, color: '#854d0e', wordBreak: 'break-all' }}>
                                {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Product Images Gallery */}
                  {selectedProductDetails.productImage && (selectedProductDetails.productImage.images || []).length > 0 && (
                    <div className="alibaba-section" style={{ marginLeft: 0, marginRight: 0 }}>
                      <div className="alibaba-section-header">
                        <div className="alibaba-section-title">🖼️ Product Images ({(selectedProductDetails.productImage?.images || []).length})</div>
                      </div>
                      <div className="alibaba-section-content">
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                          {(selectedProductDetails.productImage?.images || []).map((img, idx) => (
                            <div
                              key={idx}
                              style={{
                                width: '70px',
                                height: '70px',
                                borderRadius: '6px',
                                overflow: 'hidden',
                                border: '1px solid #e5e7eb',
                                background: '#f8fafc',
                                cursor: 'pointer'
                              }}
                              onClick={() => window.open(img, '_blank')}
                              title={`Image ${idx + 1} - Click to open`}
                            >
                              <img
                                src={img}
                                alt={`Product ${idx + 1}`}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Other Product Data */}
                  <div className="alibaba-section" style={{ marginLeft: 0, marginRight: 0 }}>
                    <div className="alibaba-section-header">
                      <div className="alibaba-section-title">📝 Other Product Data</div>
                    </div>
                    <div className="alibaba-section-content">
                      <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                          <thead>
                            <tr style={{ background: '#f8fafc' }}>
                              <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #e5e7eb', color: '#64748b', fontWeight: 600, width: '180px' }}>Field</th>
                              <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #e5e7eb', color: '#64748b', fontWeight: 600 }}>Value</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Object.entries(selectedProductDetails)
                              .filter(([key]) => !['productSaleInfo', 'productShippingInfo', 'sellerDataInfo', 'productAttribute', 'tagInfoList', 'productFeature', 'productImage', 'productSkuInfos', 'skuInfos', '_selectedInfo', 'variations', 'promotionUrl', 'subject', 'companyName'].includes(key))
                              .filter(([key, value]) => value !== null && value !== undefined && value !== '')
                              .map(([key, value], idx) => (
                                <tr key={key} style={{ background: idx % 2 === 0 ? '#fff' : '#f8fafc' }}>
                                  <td style={{ padding: '8px 12px', borderBottom: '1px solid #e5e7eb', color: '#64748b', fontWeight: 500, textTransform: 'uppercase', fontSize: '10px' }}>
                                    {key}
                                  </td>
                                  <td style={{ padding: '8px 12px', borderBottom: '1px solid #e5e7eb', color: '#1e293b', wordBreak: 'break-all' }}>
                                    {typeof value === 'object'
                                      ? (Array.isArray(value) ? `[${value.length} items]` : JSON.stringify(value).substring(0, 300))
                                      : String(value).substring(0, 300)}
                                    {(typeof value === 'string' && value.length > 300) && '...'}
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                </div>
              )}
            </>
          ) : (
            <>
              {/* Similar Products Grid */}
              <div className="apd-sidebar-header">
                <h3>Similar Products from BTPL/Global</h3>
                <button className="apd-sidebar-close" onClick={() => setShowSimilar(false)}>×</button>
              </div>

              {/* Top Pagination */}
              {similarProducts.length > 0 && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '10px 16px',
                  backgroundColor: '#fff',
                  borderBottom: '1px solid #e2e8f0',
                  gap: '8px',
                  flexWrap: 'wrap'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{ fontSize: '13px', color: '#1e293b', fontWeight: 600 }}>
                      {similarProducts.length} similar products found
                    </span>

                    {/* Sort Dropdown */}
                    <select
                      value={sortOption}
                      onChange={(e) => setSortOption(e.target.value)}
                      style={{
                        padding: '4px 8px',
                        fontSize: '11px',
                        fontWeight: 500,
                        border: '1px solid #e2e8f0',
                        borderRadius: '4px',
                        backgroundColor: '#fff',
                        color: '#475569',
                        cursor: 'pointer',
                        outline: 'none'
                      }}
                    >
                      <option value="default">Sort: Default</option>
                      <option value="price-low">Price: Low to High</option>
                      <option value="price-high">Price: High to Low</option>
                      <option value="margin-high">Margin: High to Low</option>
                      <option value="margin-low">Margin: Low to High</option>
                    </select>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <button
                      onClick={() => handleFindSimilar(currentPage - 1)}
                      disabled={currentPage <= 1 || loadingSimilar}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: currentPage <= 1 ? '#f1f5f9' : '#3b82f6',
                        color: currentPage <= 1 ? '#94a3b8' : '#fff',
                        border: 'none',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: 500,
                        cursor: currentPage <= 1 ? 'not-allowed' : 'pointer'
                      }}
                    >
                      Prev
                    </button>
                    <span style={{ fontSize: '12px', color: '#64748b' }}>
                      {currentPage} / {totalPages}
                    </span>
                    <button
                      onClick={() => handleFindSimilar(currentPage + 1)}
                      disabled={currentPage >= totalPages || loadingSimilar}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: currentPage >= totalPages ? '#f1f5f9' : '#3b82f6',
                        color: currentPage >= totalPages ? '#94a3b8' : '#fff',
                        border: 'none',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: 500,
                        cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer'
                      }}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}

              {loadingSimilar ? (
                <div className="apd-sidebar-loading">
                  <FaSpinner className="apd-spinner" />
                  <p>{findSimilarStatus || 'Searching on BTPL/Global...'}</p>
                  <div className="apd-find-progress-wrap">
                    <div className="apd-find-progress-bar" style={{ width: `${findSimilarProgress}%` }} />
                  </div>
                  <span className="apd-find-progress-text">{findSimilarProgress}%</span>
                </div>
              ) : similarProducts.length > 0 ? (
                <>
                  {/* Failed Order Summary - Segregated at Top */}
                  {failedOrderAmount && failedOrderAmount.totalAmount > 0 && (
                    <div className="apd-order-summary">
                      <div className="apd-order-summary-title">Failed Order Demand for ASIN: {asinFromQuery}</div>
                      <div className="apd-order-summary-grid">
                        <div className="apd-order-stat">
                          <div className="apd-order-stat-label">Total Value</div>
                          <div className="apd-order-stat-value">₹{failedOrderAmount.totalAmount.toLocaleString('en-IN')}</div>
                        </div>
                        <div className="apd-order-stat">
                          <div className="apd-order-stat-label">Orders</div>
                          <div className="apd-order-stat-value">{failedOrderAmount.orderCount}</div>
                        </div>
                        <div className="apd-order-stat">
                          <div className="apd-order-stat-label">Avg Order Value</div>
                          <div className="apd-order-stat-value">₹{(failedOrderAmount.totalAmount / failedOrderAmount.orderCount).toFixed(0)}</div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="apd-similar-grid">
                    {sortedSimilarProducts.slice(0, 6).map((product, index) => {
                      const orderCount = failedOrderAmount ? failedOrderAmount.orderCount : 0;
                      const avgOrderValue = failedOrderAmount ? failedOrderAmount.totalAmount / failedOrderAmount.orderCount : 0;
                      const adjustedOrderValue = avgOrderValue * 0.7; // Apply 30% reduction
                      const marginPerOrder = adjustedOrderValue - product.price.inr;
                      const totalMargin = marginPerOrder * orderCount;
                      const marginPercent = adjustedOrderValue > 0 ? ((marginPerOrder / adjustedOrderValue) * 100).toFixed(0) : 0;
                      const isPositive = marginPerOrder > 0;
                      // Match prediction - higher for first results, decreases as you go down (only for default sort)
                      const matchPercent = sortOption === 'default' ? Math.max(95 - (index * 3), 45) : null;

                      return (
                        <div
                          key={product.id}
                          className="apd-pcard"
                          onClick={() => openProductVariants(product)}
                        >
                          {/* Image with Match Prediction Badge */}
                          <div className="apd-pcard-img-wrap">
                            <img
                              src={product.image}
                              alt={product.title}
                              className="apd-pcard-img"
                              onError={(e) => { e.currentTarget.src = noImage; }}
                            />
                            {/* Match Prediction Badge - Top Right (only show for default sort) */}
                            {matchPercent !== null && (
                              <div style={{
                                position: 'absolute',
                                top: '6px',
                                right: '6px',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                fontSize: '11px',
                                fontWeight: 700,
                                backgroundColor: matchPercent >= 80 ? '#dcfce7' : matchPercent >= 60 ? '#fef3c7' : '#fee2e2',
                                color: matchPercent >= 80 ? '#16a34a' : matchPercent >= 60 ? '#d97706' : '#dc2626',
                                border: `1px solid ${matchPercent >= 80 ? '#bbf7d0' : matchPercent >= 60 ? '#fcd34d' : '#fecaca'}`,
                                zIndex: 2
                              }}>
                                {matchPercent}%
                              </div>
                            )}
                            {/* Sort indicator badge when sorting by margin/price */}
                            {sortOption !== 'default' && (
                              <div style={{
                                position: 'absolute',
                                top: '6px',
                                right: '6px',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                fontSize: '10px',
                                fontWeight: 600,
                                backgroundColor: sortOption.includes('margin')
                                  ? (isPositive ? '#dcfce7' : '#fee2e2')
                                  : '#e0f2fe',
                                color: sortOption.includes('margin')
                                  ? (isPositive ? '#16a34a' : '#dc2626')
                                  : '#0284c7',
                                border: `1px solid ${sortOption.includes('margin')
                                  ? (isPositive ? '#bbf7d0' : '#fecaca')
                                  : '#7dd3fc'}`,
                                zIndex: 2
                              }}>
                                #{index + 1}
                              </div>
                            )}
                          </div>

                          {/* Variant Badge */}
                          <div className="apd-pcard-variants">
                            {product.variantCount || 1} Variants
                          </div>

                          {/* Title */}
                          <div className="apd-pcard-title" title={product.title}>
                            {product.title}
                          </div>

                          {/* Prices */}
                          <div className="apd-pcard-prices">
                            <span className="apd-pcard-price-inr">₹{product.price.inr.toFixed(0)}</span>
                          </div>

                          {/* Profit Margin - Per Order & Total */}
                          {failedOrderAmount && failedOrderAmount.totalAmount > 0 && product.price.inr > 0 && (
                            <div style={{
                              marginTop: '6px',
                              padding: '8px',
                              backgroundColor: isPositive ? '#f0fdf4' : '#fef2f2',
                              borderRadius: '4px',
                              border: `1px solid ${isPositive ? '#bbf7d0' : '#fecaca'}`
                            }}>
                              {/* Order Value vs Source Price */}
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px', paddingBottom: '6px', borderBottom: `1px dashed ${isPositive ? '#bbf7d0' : '#fecaca'}` }}>
                                <div style={{ textAlign: 'left' }}>
                                  <div style={{ fontSize: '9px', color: '#64748b' }}>Order Value (-30%)</div>
                                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>₹{adjustedOrderValue.toFixed(0)}</div>
                                </div>
                                <div style={{ fontSize: '14px', color: '#94a3b8' }}>→</div>
                                <div style={{ textAlign: 'right' }}>
                                  <div style={{ fontSize: '9px', color: '#64748b' }}>Source Price</div>
                                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#ea580c' }}>₹{product.price.inr.toFixed(0)}</div>
                                </div>
                              </div>
                              {/* Per Order Margin */}
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                <span style={{ fontSize: '10px', color: '#64748b' }}>Margin/Order</span>
                                <span style={{ fontSize: '12px', fontWeight: 600, color: isPositive ? '#16a34a' : '#dc2626' }}>
                                  ₹{marginPerOrder.toFixed(0)} ({isPositive ? '+' : ''}{marginPercent}%)
                                </span>
                              </div>
                              {/* Total Margin */}
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontSize: '10px', color: '#64748b' }}>Total ({orderCount} orders)</span>
                                <span style={{ fontSize: '14px', fontWeight: 700, color: isPositive ? '#16a34a' : '#dc2626' }}>
                                  ₹{totalMargin.toLocaleString('en-IN')}
                                </span>
                              </div>
                            </div>
                          )}

                          {/* Meta Row */}
                          <div className="apd-pcard-meta">
                            <span className="apd-pcard-moq">MOQ: {product.moq || 1}</span>
                            <span className="apd-pcard-rating">
                              <FaStar /> {product.rating || (Math.random() * 1.5 + 3.5).toFixed(1)}
                            </span>
                          </div>

                          {/* Actions - Show on Hover */}
                          <div className="apd-pcard-actions">
                            <button className="apd-pcard-btn" onClick={(e) => { e.stopPropagation(); openProductVariants(product); }}>
                              View Variants
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Bottom Pagination */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    padding: '12px 16px',
                    backgroundColor: '#fff',
                    borderTop: '1px solid #e2e8f0',
                    gap: '12px'
                  }}>
                    <button
                      onClick={() => handleFindSimilar(currentPage - 1)}
                      disabled={currentPage <= 1 || loadingSimilar}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: currentPage <= 1 ? '#f1f5f9' : '#3b82f6',
                        color: currentPage <= 1 ? '#94a3b8' : '#fff',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: 500,
                        cursor: currentPage <= 1 ? 'not-allowed' : 'pointer'
                      }}
                    >
                      ← Previous
                    </button>
                    <span style={{ fontSize: '13px', color: '#64748b', fontWeight: 500 }}>
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      onClick={() => handleFindSimilar(currentPage + 1)}
                      disabled={currentPage >= totalPages || loadingSimilar}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: currentPage >= totalPages ? '#f1f5f9' : '#3b82f6',
                        color: currentPage >= totalPages ? '#94a3b8' : '#fff',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: 500,
                        cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer'
                      }}
                    >
                      Next →
                    </button>
                  </div>
                </>
              ) : (
                <div className="apd-sidebar-empty">
                  <p>No similar products found</p>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Confirmation Modal - Before Mapping */}
      {showConfirmModal && confirmVariant && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            backgroundColor: '#fff',
            borderRadius: '10px',
            padding: '16px 20px',
            maxWidth: '580px',
            width: '90%',
            maxHeight: '85vh',
            overflow: 'auto',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
          }}>
            {/* Header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '12px',
              paddingBottom: '10px',
              borderBottom: '1px solid #e5e7eb'
            }}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#1e293b' }}>
                Confirm ASIN Mapping
              </h3>
              <button
                onClick={() => { setShowConfirmModal(false); setConfirmVariant(null); }}
                style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#666', padding: '0 4px', lineHeight: 1 }}
              >×</button>
            </div>

            {/* Mapping Summary - Compact */}
            <div style={{ fontSize: '10px', fontWeight: 600, color: '#64748b', marginBottom: '6px', textTransform: 'uppercase' }}>
              Mapping Summary
            </div>

            {/* ASIN & Offer ID - Single Row */}
            <div style={{ display: 'flex', gap: '16px', marginBottom: '10px', padding: '8px 10px', backgroundColor: '#f8fafc', borderRadius: '6px' }}>
              <div>
                <span style={{ fontSize: '9px', color: '#64748b' }}>ASIN: </span>
                <span style={{ fontSize: '12px', fontWeight: 600, color: '#1e293b', fontFamily: 'monospace' }}>{asinFromQuery}</span>
              </div>
              <div>
                <span style={{ fontSize: '9px', color: '#64748b' }}>Offer ID: </span>
                <span style={{ fontSize: '12px', fontWeight: 600, color: '#ea580c', fontFamily: 'monospace' }}>{selectedProductDetails?._selectedInfo?.offerId}</span>
              </div>
            </div>

            {/* Selected Variant - Compact with Weight/Dimensions */}
            <div style={{ padding: '10px', backgroundColor: '#eff6ff', borderRadius: '6px', border: '1px solid #bfdbfe', marginBottom: '10px' }}>
              <div style={{ fontSize: '9px', color: '#3b82f6', marginBottom: '6px', fontWeight: 600, textTransform: 'uppercase' }}>Selected Variant</div>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                <img
                  src={confirmVariant.image || selectedProductDetails?._selectedInfo?.image || noImage}
                  alt={confirmVariant.attributeText}
                  style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '4px', border: '1px solid #bfdbfe' }}
                  onError={(e) => { e.currentTarget.src = noImage; }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  {/* SKU ID + Attributes in one row */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginBottom: '4px' }}>
                    <span style={{ fontSize: '10px', fontWeight: 600, color: '#1e293b', fontFamily: 'monospace', backgroundColor: '#e0e7ff', padding: '1px 5px', borderRadius: '3px' }}>
                      SKU: {confirmVariant.skuId || confirmVariant.id || 'N/A'}
                    </span>
                    {confirmVariant.attributes && confirmVariant.attributes.length > 0 && confirmVariant.attributes.map((attr, idx) => (
                      <span key={idx} style={{ fontSize: '10px', backgroundColor: '#fff', padding: '1px 5px', borderRadius: '3px', border: '1px solid #c7d2fe' }}>
                        <span style={{ color: '#64748b' }}>{attr.name}:</span> <span style={{ fontWeight: 500, color: '#1e293b' }}>{attr.value}</span>
                      </span>
                    ))}
                  </div>
                  {/* Price */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <span style={{ fontSize: '13px', fontWeight: 700, color: '#1e293b' }}>₹{confirmVariant.price.inr.toFixed(0)}</span>
                  </div>
                  {/* Weight & Dimensions */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    {confirmVariant.weight > 0 && (
                      <span style={{ fontSize: '9px', backgroundColor: '#fef3c7', color: '#92400e', padding: '2px 5px', borderRadius: '3px' }}>
                        ⚖️ {confirmVariant.weight.toFixed(2)}kg
                      </span>
                    )}
                    {confirmVariant.dimensions && confirmVariant.dimensions.length > 0 && (
                      <span style={{ fontSize: '9px', backgroundColor: '#e0e7ff', color: '#3730a3', padding: '2px 5px', borderRadius: '3px' }}>
                        📦 {confirmVariant.dimensions.length}×{confirmVariant.dimensions.width}×{confirmVariant.dimensions.height}cm
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Orders Table with Frozen Header */}
            {failedOrderAmount && failedOrderAmount.totalAmount > 0 && (
              <div style={{ marginBottom: '10px' }}>
                <div style={{ fontSize: '10px', fontWeight: 600, color: '#92400e', marginBottom: '6px', textTransform: 'uppercase' }}>
                  Failed Orders to Convert ({failedOrderAmount.orderCount})
                </div>

                {/* Table Container with Frozen Header */}
                <div style={{ border: '1px solid #e5e7eb', borderRadius: '6px', overflow: 'hidden' }}>
                  {/* Frozen Header */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr 1fr', backgroundColor: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>
                    <div style={{ padding: '6px 8px', fontSize: '10px', fontWeight: 600, color: '#64748b' }}>Order ID</div>
                    <div style={{ padding: '6px 8px', fontSize: '10px', fontWeight: 600, color: '#64748b', textAlign: 'right' }}>Order Value (-30%)</div>
                    <div style={{ padding: '6px 8px', fontSize: '10px', fontWeight: 600, color: '#64748b', textAlign: 'right' }}>Source</div>
                    <div style={{ padding: '6px 8px', fontSize: '10px', fontWeight: 600, color: '#64748b', textAlign: 'right' }}>Margin</div>
                  </div>
                  {/* Scrollable Body */}
                  <div style={{ maxHeight: '120px', overflowY: 'auto' }}>
                    {(failedOrderAmount.orders || []).map((order, idx) => {
                      const orderValue = (order.consumerPrice || 0) * (order.quantity || 1);
                      const adjustedOrderValue = orderValue * 0.7; // 30% reduction
                      const sourcePrice = confirmVariant.price.inr * (order.quantity || 1);
                      const margin = adjustedOrderValue - sourcePrice;
                      const marginPercent = adjustedOrderValue > 0 ? ((margin / adjustedOrderValue) * 100).toFixed(0) : 0;
                      const isPositive = margin > 0;

                      return (
                        <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr 1fr', borderBottom: '1px solid #f3f4f6' }}>
                          <div style={{ padding: '5px 8px', fontSize: '10px', color: '#374151', fontFamily: 'monospace' }}>{order.orderId?.slice(-12) || '-'}</div>
                          <div style={{ padding: '5px 8px', fontSize: '11px', color: '#1e293b', fontWeight: 500, textAlign: 'right' }}>₹{adjustedOrderValue.toLocaleString('en-IN')}</div>
                          <div style={{ padding: '5px 8px', fontSize: '11px', color: '#ea580c', fontWeight: 500, textAlign: 'right' }}>₹{sourcePrice.toFixed(0)}</div>
                          <div style={{ padding: '5px 8px', fontSize: '11px', fontWeight: 600, textAlign: 'right', color: isPositive ? '#16a34a' : '#dc2626' }}>
                            ₹{margin.toFixed(0)} <span style={{ fontSize: '9px', opacity: 0.8 }}>({isPositive ? '+' : ''}{marginPercent}%)</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Gross Total - Compact */}
                {(() => {
                  const totalSourcePrice = confirmVariant.price.inr * failedOrderAmount.totalQuantity;
                  const adjustedTotalAmount = failedOrderAmount.totalAmount * 0.7; // 30% reduction
                  const grossTotalMargin = adjustedTotalAmount - totalSourcePrice;
                  const grossMarginPercent = adjustedTotalAmount > 0 ? ((grossTotalMargin / adjustedTotalAmount) * 100).toFixed(1) : 0;
                  const isPositive = grossTotalMargin > 0;

                  return (
                    <div style={{
                      marginTop: '8px',
                      padding: '8px 10px',
                      backgroundColor: isPositive ? '#f0fdf4' : '#fef2f2',
                      borderRadius: '6px',
                      border: `1px solid ${isPositive ? '#bbf7d0' : '#fecaca'}`,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: '8px'
                    }}>
                      <div style={{ display: 'flex', gap: '16px' }}>
                        <div>
                          <span style={{ fontSize: '9px', color: '#64748b' }}>Total Value (-30%): </span>
                          <span style={{ fontSize: '12px', fontWeight: 700, color: '#1e293b' }}>₹{adjustedTotalAmount.toLocaleString('en-IN')}</span>
                        </div>
                        <div>
                          <span style={{ fontSize: '9px', color: '#64748b' }}>Source: </span>
                          <span style={{ fontSize: '12px', fontWeight: 700, color: '#ea580c' }}>₹{totalSourcePrice.toLocaleString('en-IN')}</span>
                        </div>
                        <div>
                          <span style={{ fontSize: '9px', color: '#64748b' }}>Qty: </span>
                          <span style={{ fontSize: '12px', fontWeight: 700, color: '#64748b' }}>{failedOrderAmount.totalQuantity}</span>
                        </div>
                      </div>
                      <div>
                        <span style={{ fontSize: '10px', fontWeight: 600, color: isPositive ? '#166534' : '#991b1b' }}>GROSS MARGIN: </span>
                        <span style={{ fontSize: '14px', fontWeight: 700, color: isPositive ? '#16a34a' : '#dc2626' }}>
                          ₹{grossTotalMargin.toLocaleString('en-IN')} ({isPositive ? '+' : ''}{grossMarginPercent}%)
                        </span>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* HSN Category Selection - Required */}
            <div style={{ marginBottom: '12px' }}>
              <div style={{ fontSize: '10px', fontWeight: 600, color: '#64748b', marginBottom: '6px', textTransform: 'uppercase' }}>
                HSN Mapping <span style={{ color: '#dc2626' }}>*</span>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: 500, color: '#374151', marginBottom: '4px' }}>
                  Category <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <select
                  value={selectedHsnCategory}
                  onChange={(e) => setSelectedHsnCategory(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 10px',
                    border: `1px solid ${selectedHsnCategory ? '#d1d5db' : '#fca5a5'}`,
                    borderRadius: '5px',
                    fontSize: '12px',
                    backgroundColor: selectedHsnCategory ? '#fff' : '#fef2f2',
                    cursor: 'pointer'
                  }}
                >
                  <option value="">Select Category</option>
                  {hsnCategories.map((cat, idx) => (
                    <option key={idx} value={cat}>{cat}</option>
                  ))}
                </select>
                {!selectedHsnCategory && (
                  <div style={{ fontSize: '10px', color: '#dc2626', marginTop: '2px' }}>HSN Category is required</div>
                )}
              </div>

              {/* HSN Details Card - Shows automatically when category selected */}
              {loadingHsn ? (
                <div style={{ marginTop: '8px', padding: '10px', backgroundColor: '#f8fafc', borderRadius: '6px', textAlign: 'center' }}>
                  <FaSpinner className="apd-spinner" style={{ width: '14px', height: '14px' }} />
                  <span style={{ fontSize: '11px', color: '#64748b', marginLeft: '6px' }}>Loading HSN details...</span>
                </div>
              ) : selectedHsnMapping && (
                <div style={{
                  marginTop: '8px',
                  padding: '10px',
                  backgroundColor: '#f0fdf4',
                  borderRadius: '6px',
                  border: '1px solid #bbf7d0'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <span style={{
                      fontSize: '14px',
                      fontWeight: 700,
                      color: '#166534',
                      fontFamily: 'monospace',
                      backgroundColor: '#dcfce7',
                      padding: '2px 8px',
                      borderRadius: '4px'
                    }}>
                      HSN: {selectedHsnMapping.hsnCode || selectedHsnMapping.HSN}
                    </span>
                    <span style={{ fontSize: '11px', color: '#166534' }}>
                      {selectedHsnMapping.category || selectedHsnMapping.Item}
                    </span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
                    <div style={{ textAlign: 'center', padding: '6px', backgroundColor: '#fff', borderRadius: '4px' }}>
                      <div style={{ fontSize: '9px', color: '#64748b' }}>BCD</div>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>
                        {selectedHsnMapping.bcd !== undefined ? `${(selectedHsnMapping.bcd * 100).toFixed(1)}%` :
                          selectedHsnMapping.BCD !== undefined ? `${(selectedHsnMapping.BCD * 100).toFixed(1)}%` : '0%'}
                      </div>
                    </div>
                    <div style={{ textAlign: 'center', padding: '6px', backgroundColor: '#fff', borderRadius: '4px' }}>
                      <div style={{ fontSize: '9px', color: '#64748b' }}>Surcharge</div>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>
                        {selectedHsnMapping.surcharge !== undefined ? `${(selectedHsnMapping.surcharge * 100).toFixed(1)}%` :
                          selectedHsnMapping.Surcharge !== undefined ? `${(selectedHsnMapping.Surcharge * 100).toFixed(1)}%` : '0%'}
                      </div>
                    </div>
                    <div style={{ textAlign: 'center', padding: '6px', backgroundColor: '#fff', borderRadius: '4px' }}>
                      <div style={{ fontSize: '9px', color: '#64748b' }}>Custom Duty</div>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: '#ea580c' }}>
                        {selectedHsnMapping.customsDutyRate ||
                          (selectedHsnMapping.FinalCustomDuty !== undefined ? `${(selectedHsnMapping.FinalCustomDuty * 100).toFixed(1)}%` : '0%')}
                      </div>
                    </div>
                    <div style={{ textAlign: 'center', padding: '6px', backgroundColor: '#fff', borderRadius: '4px' }}>
                      <div style={{ fontSize: '9px', color: '#64748b' }}>IGST</div>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: '#16a34a' }}>
                        {selectedHsnMapping.igstRate ||
                          (selectedHsnMapping.IGST !== undefined ? `${(selectedHsnMapping.IGST * 100).toFixed(1)}%` : '18%')}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Remark - After HSN */}
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: 500, color: '#374151', marginBottom: '4px' }}>
                Remark <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <textarea
                value={mappingRemark}
                onChange={(e) => setMappingRemark(e.target.value)}
                placeholder="Add notes about this mapping (required)..."
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  border: `1px solid ${mappingRemark.trim() ? '#d1d5db' : '#fca5a5'}`,
                  borderRadius: '5px',
                  fontSize: '12px',
                  resize: 'vertical',
                  minHeight: '50px',
                  fontFamily: 'inherit',
                  backgroundColor: mappingRemark.trim() ? '#fff' : '#fef2f2'
                }}
              />
              {!mappingRemark.trim() && (
                <div style={{ fontSize: '10px', color: '#dc2626', marginTop: '2px' }}>Remark is required</div>
              )}
            </div>

            {/* Action Buttons - Compact */}
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setShowConfirmModal(false); setConfirmVariant(null); }}
                disabled={isMapping}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#f3f4f6',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  borderRadius: '5px',
                  fontSize: '13px',
                  fontWeight: 500,
                  cursor: isMapping ? 'not-allowed' : 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleMapVariant}
                disabled={isMapping || !mappingRemark.trim() || !selectedHsnMapping}
                style={{
                  padding: '8px 20px',
                  backgroundColor: (isMapping || !mappingRemark.trim() || !selectedHsnMapping) ? '#9ca3af' : '#3b82f6',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '5px',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: (isMapping || !mappingRemark.trim() || !selectedHsnMapping) ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                {isMapping ? (
                  <>
                    <FaSpinner className="apd-spinner" style={{ width: '12px', height: '12px' }} />
                    Mapping...
                  </>
                ) : (
                  'Confirm & Map'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
